import {
  FEEDBACK_AREAS,
  FEEDBACK_SEVERITIES,
  FEEDBACK_TYPES,
  type FeedbackArea,
  type FeedbackAssistSpend,
  type FeedbackAssistState,
  type FeedbackBatchProgress,
  type FeedbackGroupDraft,
  type FeedbackGroupItem,
  type FeedbackSeverity,
  type FeedbackSuggestion,
  type FeedbackSuggestionDecision,
  type FeedbackType,
  type OperatorFeedbackItem,
  type SuggestFeedbackBatch,
} from "@esi/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, RotateCw, ShieldOff, Sparkles } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatUsd } from "@/components/charts";
import { NativeSelect } from "@/components/NativeSelect";
import { Notice } from "@/components/Notice";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "../api.ts";
import { ApiError, apiError, ok } from "../api-errors.ts";
import { formatDate, formatNumber } from "../i18n/format.ts";
import { t as i18nT } from "../i18n/i18n.ts";
import { consoleFeedbackGroupPath, consoleFeedbackPath, Link } from "../router.tsx";
import { useCsrfHeaders } from "./operator.tsx";

/**
 * Assisted triage in the console (openspec change `feedback-triage-assist`): the report view's
 * suggestion, the inbox's "Suggested: <title>" and "Suggest for Unsorted", and the group page's
 * draft, Severity and Area. Every suggestion waits for an operator; an older API (`404`) hides
 * the assistant.
 */

const operatorApi = api.api.operator;
const keys = {
  state: (reportId: string) => ["console", "feedback", "assist", reportId] as const,
  spend: ["console", "feedback", "assist-spend"] as const,
  groups: ["console", "feedback", "groups"] as const,
};

/** A route the live API does not have yet (canary of an older release): no assistant. */
const absent = (error: unknown) => error instanceof ApiError && error.status === 404;
const budgetReached = (error: unknown) =>
  error instanceof ApiError && error.code === "triage_budget_reached";

const fieldLink =
  "font-medium text-fg underline underline-offset-4 decoration-line-strong hover:decoration-current";

// ── Today's spend ──────────────────────────────────────────────────────────────────────────

export function useAssistSpend() {
  return useQuery({
    queryKey: keys.spend,
    queryFn: async (): Promise<FeedbackAssistSpend> =>
      ok(await operatorApi["feedback-assist"].spend.$get()),
    retry: false,
  });
}

/** "Triage assistant today: $0.42 · alerts at $5.00", and the cap when one is set. */
function SpendLine({ spend }: { spend: FeedbackAssistSpend }) {
  const { t } = useTranslation();
  return (
    <p className="tabular text-xs text-fg-muted">
      {t("console.feedback.assist.spend", {
        usd: formatUsd(spend.usd),
        alert: formatUsd(spend.alertUsd),
      })}
      {spend.capUsd !== null
        ? ` · ${t("console.feedback.assist.spendCap", { cap: formatUsd(spend.capUsd) })}`
        : null}
    </p>
  );
}

// ── The report view's suggestion (task 4.1) ────────────────────────────────────────────────

/**
 * The assistant's suggestion for an Unsorted report: asked the first time the report opens
 * (cached by the API, so reopening costs nothing), shown until an operator decides it.
 */
export function AssistantSuggestion({ reportId }: { reportId: string }) {
  const { t } = useTranslation();
  const headers = useCsrfHeaders();
  const queryClient = useQueryClient();
  const state = useQuery({
    queryKey: keys.state(reportId),
    queryFn: async (): Promise<FeedbackAssistState> =>
      ok(
        await operatorApi.feedback[":reportId"].suggest.$post(
          { param: { reportId }, json: { again: false } },
          headers,
        ),
      ),
    retry: false,
    refetchOnWindowFocus: false,
    // Revalidated on each opening: the API serves a valid cached suggestion without a model
    // call, and passes again only when its group has closed.
    refetchOnMount: "always",
  });
  const again = useMutation({
    mutationFn: async () =>
      ok(
        await operatorApi.feedback[":reportId"].suggest.$post(
          { param: { reportId }, json: { again: true } },
          headers,
        ),
      ),
    onSuccess: (next) => {
      queryClient.setQueryData(keys.state(reportId), next);
      void queryClient.invalidateQueries({ queryKey: keys.spend });
    },
  });
  if (state.isError && absent(state.error)) {
    return null;
  }
  const body = state.isPending ? (
    <p role="status" className="flex items-center gap-2 text-sm text-fg-secondary">
      <Loader2 aria-hidden="true" className="size-4 animate-spin motion-reduce:animate-none" />
      {t("console.feedback.assist.thinking")}
    </p>
  ) : state.isError ? (
    <Notice tone={budgetReached(state.error) ? "warning" : "danger"}>
      {budgetReached(state.error)
        ? t("console.feedback.assist.budgetReached")
        : state.error.message}
    </Notice>
  ) : state.data.optedOut ? (
    <p className="flex items-start gap-2 text-sm text-fg-secondary">
      <ShieldOff aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-fg-muted" />
      {t("console.feedback.assist.optedOut")}
    </p>
  ) : !state.data.suggestion || state.data.suggestion.status === "rejected" ? (
    <p className="text-sm text-fg-secondary">{t("console.feedback.assist.rejected")}</p>
  ) : state.data.suggestion.status !== "pending" ? null : (
    <SuggestionBody suggestion={state.data.suggestion} reportId={reportId} />
  );
  const pending = state.data?.suggestion?.status === "pending";
  return (
    <section aria-labelledby="feedback-assist" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="feedback-assist" className="flex items-center gap-2 text-sm font-semibold text-fg">
          <Sparkles aria-hidden="true" className="size-4 text-fg-muted" />
          {t("console.feedback.assist.title")}
        </h2>
        {pending ? (
          <Button
            variant="ghost"
            size="xs"
            disabled={again.isPending}
            onClick={() => again.mutate()}
          >
            <RotateCw
              aria-hidden="true"
              className={again.isPending ? "animate-spin motion-reduce:animate-none" : undefined}
            />
            {t("console.feedback.assist.again")}
          </Button>
        ) : null}
      </div>
      {again.isError ? (
        <Notice tone={budgetReached(again.error) ? "warning" : "danger"}>
          {budgetReached(again.error)
            ? t("console.feedback.assist.budgetReached")
            : again.error.message}
        </Notice>
      ) : null}
      {body}
    </section>
  );
}

function SuggestionBody({
  suggestion,
  reportId,
}: {
  suggestion: FeedbackSuggestion;
  reportId: string;
}) {
  const { t } = useTranslation();
  const headers = useCsrfHeaders();
  const queryClient = useQueryClient();
  const [changing, setChanging] = useState(false);
  const decide = useMutation({
    mutationFn: async (decision: FeedbackSuggestionDecision) =>
      ok(
        await operatorApi["feedback-suggestions"][":suggestionId"].decision.$post(
          { param: { suggestionId: suggestion.id }, json: decision },
          headers,
        ),
      ),
    onSuccess: async (decided) => {
      queryClient.setQueryData<FeedbackAssistState>(keys.state(reportId), (prev) =>
        prev?.suggestion
          ? { ...prev, suggestion: { ...prev.suggestion, status: decided.status } }
          : prev,
      );
      // The report's group, the header and the inbox follow the move.
      await queryClient.invalidateQueries({
        queryKey: ["console", "feedback"],
        predicate: (q) => q.queryKey[2] !== "assist" && q.queryKey[2] !== "report",
      });
    },
  });
  const seen = { seen: suggestion.createdAt };
  const groupTitle = suggestion.group?.title ?? suggestion.newGroupTitle;
  const facts: [string, React.ReactNode][] = [
    [
      t("console.feedback.assist.group"),
      suggestion.group ? (
        <span key="group">
          <Link href={consoleFeedbackGroupPath(suggestion.group.id)} className={fieldLink}>
            {suggestion.group.title}
          </Link>{" "}
          <span className="tabular text-fg-muted">
            ·{" "}
            {t("console.feedback.assist.groupMeta", {
              type: t(`feedback.types.${suggestion.group.type}`),
              count: suggestion.group.reports,
            })}
          </span>
        </span>
      ) : suggestion.newGroupTitle ? (
        <span key="group">
          {suggestion.newGroupTitle}{" "}
          <span className="text-fg-muted">· {t("console.feedback.assist.newGroup")}</span>
        </span>
      ) : (
        <span key="group" className="text-fg-muted">
          {t("console.feedback.assist.noGroup")}
        </span>
      ),
    ],
    [
      t("console.feedback.assist.duplicates"),
      suggestion.duplicates.length > 0 ? (
        <span key="duplicates" className="flex flex-wrap gap-x-1">
          {suggestion.duplicates.map((d, i) => (
            <span key={d.id}>
              <Link href={consoleFeedbackPath(d.id)} className={fieldLink}>
                {t("console.feedback.assist.duplicate", {
                  type: t(`feedback.types.${d.type}`),
                  tenant: d.tenantName,
                  date: formatDate(d.createdAt),
                })}
              </Link>
              {i < suggestion.duplicates.length - 1 ? "," : null}
            </span>
          ))}
        </span>
      ) : (
        <span key="duplicates" className="text-fg-muted">
          {t("console.feedback.assist.noDuplicates")}
        </span>
      ),
    ],
    [t("console.feedback.assist.type"), t(`feedback.types.${suggestion.type}`)],
    [
      t("console.feedback.assist.severity"),
      t(`console.feedback.assist.severities.${suggestion.severity}`),
    ],
    [
      t("console.feedback.assist.area"),
      <code key="area" className="font-mono text-xs">{`area::${suggestion.area}`}</code>,
    ],
    [t("console.feedback.assist.why"), suggestion.reason || "—"],
  ];
  return (
    <div className="flex flex-col gap-4">
      <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-[8rem_minmax(0,1fr)]">
        {facts.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-fg-secondary">{label}</dt>
            <dd className="min-w-0 break-words text-fg">{value}</dd>
          </div>
        ))}
      </dl>
      {changing ? (
        <ChangeSuggestion
          suggestion={suggestion}
          pending={decide.isPending}
          onCancel={() => setChanging(false)}
          onAccept={(changes) => decide.mutate({ decision: "accept", ...changes, ...seen })}
        />
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            disabled={decide.isPending || !groupTitle}
            onClick={() => decide.mutate({ decision: "accept", ...seen })}
          >
            {t("console.feedback.assist.accept")}
          </Button>
          <Button variant="outline" disabled={decide.isPending} onClick={() => setChanging(true)}>
            {t("console.feedback.assist.change")}
          </Button>
          <Button
            variant="ghost"
            disabled={decide.isPending}
            onClick={() => decide.mutate({ decision: "reject", ...seen })}
          >
            {t("console.feedback.assist.reject")}
          </Button>
        </div>
      )}
      {decide.isError ? (
        <Notice tone="danger">
          {decide.error instanceof ApiError && decide.error.code === "suggestion_decided"
            ? t("console.feedback.assist.decidedElsewhere")
            : decide.error.message}
        </Notice>
      ) : null}
      <p className="text-xs text-fg-muted">{t("console.feedback.assist.footnote")}</p>
    </div>
  );
}

const NEW_GROUP = "new";

/** "Change…": the operator's own group, type, severity and area, then accept. */
function ChangeSuggestion(props: {
  suggestion: FeedbackSuggestion;
  pending: boolean;
  onCancel: () => void;
  onAccept: (
    changes: Omit<Extract<FeedbackSuggestionDecision, { decision: "accept" }>, "decision">,
  ) => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const { suggestion } = props;
  const groups = useQuery({
    queryKey: keys.groups,
    queryFn: async (): Promise<FeedbackGroupItem[]> =>
      (await ok(await operatorApi["feedback-groups"].$get())).groups,
    retry: false,
  });
  const open = (groups.data ?? []).filter(
    (g) => !["fixed", "not_planned", "duplicate"].includes(g.status),
  );
  const [group, setGroup] = useState(suggestion.group?.id ?? NEW_GROUP);
  const [title, setTitle] = useState(suggestion.newGroupTitle ?? "");
  const [type, setType] = useState<FeedbackType>(suggestion.type);
  const [severity, setSeverity] = useState<FeedbackSeverity>(suggestion.severity);
  const [area, setArea] = useState<FeedbackArea>(suggestion.area);
  const isNew = group === NEW_GROUP;
  return (
    <form
      className="flex flex-col gap-4 rounded-lg border border-line bg-surface-raised p-4"
      aria-label={t("console.feedback.assist.changeTitle")}
      onSubmit={(e) => {
        e.preventDefault();
        // Only what the operator changed: an unchanged label never overrides the group's own.
        const sameGroup = isNew
          ? !suggestion.group && title.trim() === suggestion.newGroupTitle
          : suggestion.group?.id === group;
        props.onAccept({
          ...(!sameGroup && { group: isNew ? { new: title.trim() } : { id: group } }),
          ...(isNew && type !== suggestion.type && { type }),
          ...(severity !== suggestion.severity && { severity }),
          ...(area !== suggestion.area && { area }),
        });
      }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-group`}>{t("console.feedback.assist.group")}</Label>
          <NativeSelect
            id={`${id}-group`}
            value={group}
            onChange={(e) => setGroup(e.currentTarget.value)}
          >
            <option value={NEW_GROUP}>{t("console.feedback.assist.newGroupOption")}</option>
            {/* The suggested group first, even if the list has not loaded it. */}
            {suggestion.group && !open.some((g) => g.id === suggestion.group?.id) ? (
              <option value={suggestion.group.id}>{suggestion.group.title}</option>
            ) : null}
            {open.map((g) => (
              <option key={g.id} value={g.id}>
                {g.title}
              </option>
            ))}
          </NativeSelect>
        </div>
        {isNew ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${id}-title`}>{t("console.feedback.assist.newGroupTitle")}</Label>
            <Input
              id={`${id}-title`}
              value={title}
              maxLength={200}
              required
              onChange={(e) => setTitle(e.currentTarget.value)}
            />
          </div>
        ) : null}
        {/* An existing group keeps its own type: the type applies to a new group only. */}
        {isNew ? (
          <LabelSelect
            id={`${id}-type`}
            label={t("console.feedback.assist.type")}
            value={type}
            values={FEEDBACK_TYPES}
            text={(v) => t(`feedback.types.${v}`)}
            onChange={setType}
          />
        ) : null}
        <LabelSelect
          id={`${id}-severity`}
          label={t("console.feedback.assist.severity")}
          value={severity}
          values={FEEDBACK_SEVERITIES}
          text={(v) => t(`console.feedback.assist.severities.${v}`)}
          onChange={setSeverity}
        />
        <LabelSelect
          id={`${id}-area`}
          label={t("console.feedback.assist.area")}
          value={area}
          values={FEEDBACK_AREAS}
          text={(v) => `area::${v}`}
          onChange={setArea}
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" disabled={props.pending || (isNew && !title.trim())}>
          {t("console.feedback.assist.acceptChanges")}
        </Button>
        <Button type="button" variant="ghost" onClick={props.onCancel}>
          {t("console.feedback.assist.cancel")}
        </Button>
      </div>
    </form>
  );
}

function LabelSelect<T extends string>(props: {
  id: string;
  label: string;
  value: T;
  values: readonly T[];
  text: (value: T) => string;
  onChange: (value: T) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={props.id}>{props.label}</Label>
      <NativeSelect
        id={props.id}
        value={props.value}
        onChange={(e) => props.onChange(e.currentTarget.value as T)}
      >
        {props.values.map((v) => (
          <option key={v} value={v}>
            {props.text(v)}
          </option>
        ))}
      </NativeSelect>
    </div>
  );
}

// ── The inbox (task 4.2) ───────────────────────────────────────────────────────────────────

/**
 * "Suggested: <title>" under an Unsorted report with a pending suggestion. A new group shows a
 * "New group" chip before its title instead, so truncating a long title never cuts the marker; the
 * full line ("Suggested: <title> (new group)") is the tooltip and the accessible text.
 */
export function InboxSuggestion({ report }: { report: OperatorFeedbackItem }) {
  const { t } = useTranslation();
  if (!report.suggestion) {
    return null;
  }
  const { isNew, title } = report.suggestion;
  const full = t(
    isNew ? "console.feedback.assist.suggestedNew" : "console.feedback.assist.suggested",
    { title },
  );
  return (
    <span className="flex min-w-0 items-center gap-1.5 text-xs text-fg-secondary" title={full}>
      <Sparkles aria-hidden="true" className="size-3.5 shrink-0 text-fg-muted" />
      <span className="sr-only">{full}</span>
      <span aria-hidden="true" className="flex min-w-0 items-center gap-1.5">
        {isNew ? (
          <>
            <Badge
              variant="secondary"
              data-testid="inbox-suggestion-new"
              className="px-1.5 py-0 leading-4 font-medium"
            >
              {t("console.feedback.assist.newGroupChip")}
            </Badge>
            <span className="truncate" data-testid="inbox-suggestion-title">
              {title}
            </span>
          </>
        ) : (
          <span className="truncate" data-testid="inbox-suggestion-title">
            {full}
          </span>
        )}
      </span>
    </span>
  );
}

/**
 * "Suggest for Unsorted": up to 50 Unsorted reports of the inbox's filter, with progress as each
 * settles, and today's triage spend against the alert threshold.
 */
export function SuggestUnsorted({ filter }: { filter: SuggestFeedbackBatch }) {
  const { t } = useTranslation();
  const headers = useCsrfHeaders();
  const queryClient = useQueryClient();
  const spend = useAssistSpend();
  const [progress, setProgress] = useState<{ done: number; suggested: number; total?: number }>();
  const run = useMutation({
    mutationFn: async () => {
      const res = await operatorApi.feedback.suggest.$post({ json: filter }, headers);
      if (!res.ok || !res.body) {
        throw await apiError(res);
      }
      setProgress({ done: 0, suggested: 0 });
      const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
      let buffer = "";
      let done = 0;
      let suggested = 0;
      let budget = false;
      for (;;) {
        const chunk = await reader.read();
        if (chunk.done) {
          break;
        }
        buffer += chunk.value;
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";
        for (const line of lines.filter(Boolean)) {
          const event = JSON.parse(line) as FeedbackBatchProgress;
          if (event.kind === "report") {
            done++;
            suggested += event.outcome === "suggested" ? 1 : 0;
            budget ||= event.outcome === "budget";
            setProgress({ done, suggested });
          } else {
            setProgress({ done, suggested, total: event.total });
          }
        }
      }
      return { budget };
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ["console", "feedback"] });
    },
  });
  if (spend.isError && absent(spend.error)) {
    return null;
  }
  const capped = spend.data?.capped ?? false;
  return (
    <div className="flex flex-col gap-1.5 rounded-lg border border-line bg-surface-raised px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <div className="flex min-w-0 flex-col gap-0.5">
        <p className="text-sm text-fg-secondary" aria-live="polite">
          {run.isPending && progress
            ? t("console.feedback.assist.batchRunning", { done: formatNumber(progress.done) })
            : progress?.total !== undefined
              ? progress.total === 0
                ? t("console.feedback.assist.batchNone")
                : t("console.feedback.assist.batchDone", {
                    count: progress.suggested,
                    total: formatNumber(progress.total),
                  })
              : t("console.feedback.assist.batchHint")}
        </p>
        {run.data?.budget || capped ? (
          <p className="text-xs text-warning-fg">{t("console.feedback.assist.budgetReached")}</p>
        ) : null}
        {run.isError ? <p className="text-xs text-danger-fg">{run.error.message}</p> : null}
        {spend.data ? <SpendLine spend={spend.data} /> : null}
      </div>
      <Button
        variant="outline"
        size="sm"
        className="self-start sm:self-center"
        disabled={run.isPending || capped}
        onClick={() => run.mutate()}
      >
        {run.isPending ? (
          <Loader2 aria-hidden="true" className="animate-spin motion-reduce:animate-none" />
        ) : (
          <Sparkles aria-hidden="true" />
        )}
        {t("console.feedback.assist.batch")}
      </Button>
    </div>
  );
}

// ── The group page (task 4.3) ──────────────────────────────────────────────────────────────

/** "Draft with the assistant": fills the group's fields for editing; nothing is saved. */
export function useGroupDraft(groupId: string, onDraft: (draft: FeedbackGroupDraft) => void) {
  const headers = useCsrfHeaders();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<FeedbackGroupDraft> =>
      ok(
        await operatorApi["feedback-groups"][":groupId"].draft.$post(
          { param: { groupId } },
          headers,
        ),
      ),
    onSuccess: (draft) => {
      onDraft(draft);
      void queryClient.invalidateQueries({ queryKey: keys.spend });
    },
  });
}

export function DraftButton(props: { pending: boolean; onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <Button variant="outline" size="sm" disabled={props.pending} onClick={props.onClick}>
      {props.pending ? (
        <Loader2 aria-hidden="true" className="animate-spin motion-reduce:animate-none" />
      ) : (
        <Sparkles aria-hidden="true" />
      )}
      {props.pending ? t("console.feedback.assist.drafting") : t("console.feedback.assist.draft")}
    </Button>
  );
}

/**
 * The draft's note: how many reports it read and left out, that nothing is saved yet, and what
 * the issue preview's check finds in it (a copied run, a quote…) before the operator saves.
 */
export function DraftNote({ draft }: { draft: FeedbackGroupDraft }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2">
      <DraftUsage draft={draft} />
      {draft.findings.length > 0 ? (
        <Notice tone="warning" title={t("console.feedback.triage.findingsTitle")}>
          <ul className="list-disc pl-4">
            {draft.findings.map((f) => (
              <li key={`${f.field}-${f.kind}-${f.excerpt}`}>
                {t(`console.feedback.triage.findings.${f.kind}`, { excerpt: f.excerpt })}
              </li>
            ))}
          </ul>
        </Notice>
      ) : null}
    </div>
  );
}

function DraftUsage({ draft }: { draft: FeedbackGroupDraft }) {
  const { t } = useTranslation();
  return (
    <p role="status" className="text-xs text-fg-secondary">
      {t("console.feedback.assist.draftUsed", { count: draft.used })}
      {draft.leftOut > 0
        ? ` ${t("console.feedback.assist.draftLeftOut", { count: draft.leftOut })}`
        : null}{" "}
      {t("console.feedback.assist.draftEdit")}
    </p>
  );
}

/** Why a draft failed: the budget, every report opted out, or the error itself. */
export function draftError(error: Error): string {
  const t = i18nT;
  if (budgetReached(error)) {
    return t("console.feedback.assist.budgetReached");
  }
  if (error instanceof ApiError && error.code === "assist_no_reports") {
    return t("console.feedback.assist.draftNoReports");
  }
  return error.message;
}

/** Severity and Area on the group page, with help; "Not set" clears. */
export function GroupLabelFields(props: {
  id: string;
  severity: FeedbackSeverity | null;
  area: FeedbackArea | null;
  drafted: { severity: boolean; area: boolean };
  onSeverity: (value: FeedbackSeverity | null) => void;
  onArea: (value: FeedbackArea | null) => void;
}) {
  const { t } = useTranslation();
  const tint = (on: boolean) => (on ? "bg-accent-subtle" : undefined);
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${props.id}-severity`}>{t("console.feedback.assist.severity")}</Label>
        <NativeSelect
          id={`${props.id}-severity`}
          value={props.severity ?? ""}
          className={tint(props.drafted.severity)}
          aria-describedby={`${props.id}-severity-hint`}
          onChange={(e) =>
            props.onSeverity((e.currentTarget.value || null) as FeedbackSeverity | null)
          }
        >
          <option value="">{t("console.feedback.assist.notSet")}</option>
          {FEEDBACK_SEVERITIES.map((v) => (
            <option key={v} value={v}>
              {t(`console.feedback.assist.severities.${v}`)}
            </option>
          ))}
        </NativeSelect>
        <p id={`${props.id}-severity-hint`} className="text-xs text-fg-secondary">
          {props.severity
            ? t(`console.feedback.assist.severityHelp.${props.severity}`)
            : t("console.feedback.assist.severityHint")}
        </p>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${props.id}-area`}>{t("console.feedback.assist.area")}</Label>
        <NativeSelect
          id={`${props.id}-area`}
          value={props.area ?? ""}
          className={`font-mono text-xs ${tint(props.drafted.area) ?? ""}`}
          aria-describedby={`${props.id}-area-hint`}
          onChange={(e) => props.onArea((e.currentTarget.value || null) as FeedbackArea | null)}
        >
          <option value="">{t("console.feedback.assist.notSet")}</option>
          {FEEDBACK_AREAS.map((v) => (
            <option key={v} value={v}>
              {`area::${v}`}
            </option>
          ))}
        </NativeSelect>
        <p id={`${props.id}-area-hint`} className="text-xs text-fg-secondary">
          {t("console.feedback.assist.areaHint")}
        </p>
      </div>
    </div>
  );
}
